import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getMachine } from 'lib/selectors/getMachine';

import {
  actionSetFAIStatus,
  actionLoadFAIStatus,
  actionClearFAIStatus,
} from 'lib/actions';
import { getCompany } from 'lib/selectors/getCompany';
import { getFAIEnabled } from 'lib/selectors/getFAIEnabled';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { FAISocket } from 'lib/api/websockets/FAISocket';

const FAI_HYDRATION_TIMEOUT_MS = 10 * 1000;

const useFAIData = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const machine = useSelector(getMachine);
  const company = useSelector(getCompany);
  const faiEnabled = useSelector(getFAIEnabled);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const connectionKeyRef = useRef(null);

  const wooRef = latestActivitySet?.workOrderOperationRef;
  const companyId = company?.id;

  useEffect(() => {
    if (!faiEnabled || !machine.machineRef || !companyId) {
      connectionKeyRef.current = null;
      dispatch(actionClearFAIStatus());
      return () => {};
    }

    if (!wooRef) {
      connectionKeyRef.current = null;
      dispatch(actionSetFAIStatus({ currentInspection: null }));
      return () => {};
    }

    const connectionKey = `${machine.machineRef}:${wooRef}`;
    const connectionKeyChanged = connectionKeyRef.current !== connectionKey;
    connectionKeyRef.current = connectionKey;

    const needsHydration =
      connectionKeyChanged || !store.getState().fai?.initialized;

    let cancelled = false;

    if (needsHydration) {
      dispatch(actionLoadFAIStatus());
    }

    const settleHydration = () => {
      if (cancelled) return;
      dispatch(actionSetFAIStatus({ currentInspection: null }));
    };

    const hydrationTimeoutId = needsHydration
      ? setTimeout(settleHydration, FAI_HYDRATION_TIMEOUT_MS)
      : null;

    const channel = new FAISocket({
      companyId,
      machineRef: machine.machineRef,
      workOrderOperationRef: wooRef,
    });

    channel.subscribe(
      (data) => {
        if (Object.prototype.hasOwnProperty.call(data, 'currentInspection')) {
          if (hydrationTimeoutId != null) {
            clearTimeout(hydrationTimeoutId);
          }
          dispatch(
            actionSetFAIStatus({
              currentInspection: data.currentInspection ?? null,
            })
          );
        }
      },
      needsHydration
        ? () => {
            if (hydrationTimeoutId != null) {
              clearTimeout(hydrationTimeoutId);
            }
            settleHydration();
          }
        : null
    );

    return () => {
      cancelled = true;
      if (hydrationTimeoutId != null) {
        clearTimeout(hydrationTimeoutId);
      }
      channel.leave();
    };
  }, [dispatch, store, faiEnabled, machine.machineRef, wooRef, companyId]);
};

export { useFAIData };
